import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import ConfirmationModal from '../shared/ConfirmationModal';
import SavePromptDialog from './SavePromptDialog';
import { formatDateTime } from '../../utils/dateUtils';
import { useData } from '../../contexts/DataContext';

interface Prompt {
  id: string;
  name: string;
  content: string;
  feature: string;
  updated_at: string;
  private: boolean;
  domain_id: string | null;
  general: boolean;
}

interface PromptsDrawerProps {
  open: boolean;
  onClose: () => void;
  onApplyPrompt: (content: string) => void;
  companyId: string;
  domainId: string | null;
  feature: string;
}

const PromptsDrawer = ({ open, onClose, onApplyPrompt, domainId, feature }: PromptsDrawerProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [personalPrompts, setPersonalPrompts] = useState<Prompt[]>([]);
  const [companyPrompts, setCompanyPrompts] = useState<Prompt[]>([]);
  const [domainPrompts, setDomainPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletePromptId, setDeletePromptId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [editPrompt, setEditPrompt] = useState<Prompt | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { targetCompany: companyId } = useData();

  useEffect(() => {
    if (open && user) {
      fetchAllPrompts();
    }
  }, [open, user, feature, domainId, companyId]);

  const fetchAllPrompts = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .eq('company_id', companyId)
        .eq('feature', feature)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Split prompts into categories
      const personal = data.filter((p) => p.private === true);
      const company = data.filter((p) => p.private === false && p.domain_id === null);
      const domain = data.filter((p) => p.private === false && p.domain_id === domainId);

      setPersonalPrompts(personal || []);
      setCompanyPrompts(company || []);
      setDomainPrompts(domain || []);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      showNotification(
        error instanceof Error ? error.message : t('common.error_occurred'),
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyPrompt = (content: string) => {
    onApplyPrompt(content);
    onClose();
  };

  const handleEditClick = (prompt: Prompt) => {
    setEditPrompt(prompt);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (promptId: string) => {
    setDeletePromptId(promptId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletePromptId) return;

    setIsDeleteLoading(true);
    try {
      const { error } = await supabase.from('prompts').delete().eq('id', deletePromptId);

      if (error) throw error;

      // Remove the deleted prompt from state
      setPersonalPrompts((prompts) => prompts.filter((p) => p.id !== deletePromptId));
      setCompanyPrompts((prompts) => prompts.filter((p) => p.id !== deletePromptId));
      setDomainPrompts((prompts) => prompts.filter((p) => p.id !== deletePromptId));

      showNotification(t('common.deleted_successfully'), 'success');
    } catch (error) {
      console.error('Error deleting prompt:', error);
      showNotification(
        error instanceof Error ? error.message : t('common.error_occurred'),
        'error'
      );
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setDeletePromptId(null);
    }
  };

  const handleEditSave = () => {
    setIsEditModalOpen(false);
    setEditPrompt(null);
    fetchAllPrompts();
  };

  const renderPromptList = (prompts: Prompt[]) => {
    if (isLoading) {
      return <Typography>{t('common.loading')}</Typography>;
    }

    if (prompts.length === 0) {
      return <Typography color="text.secondary">{t('common.no_prompts_found')}</Typography>;
    }

    return (
      <List
        sx={{
          p: 0,
          maxHeight: '40vh',
          overflow: 'auto',
        }}
      >
        {prompts.map((prompt) => (
          <ListItem
            key={prompt.id}
            sx={{
              borderBottom: '1px solid #f0f0f0',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              paddingRight: '180px', // Make room for the buttons
            }}
          >
            <ListItemText primary={prompt.name} secondary={formatDateTime(prompt.updated_at)} />
            <ListItemSecondaryAction>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleApplyPrompt(prompt.content);
                }}
                sx={{ mr: 1, minWidth: '80px' }}
              >
                {t('common.use_me')}
              </Button>
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(prompt);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(prompt.id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '400px',
            padding: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 1,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6">{t('common.saved_prompts')}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            p: 2,
            maxHeight: 'calc(100vh - 56px)', // Account for the header
          }}
        >
          {/* Company-wide prompts */}
          <Accordion defaultExpanded={false} sx={{ mb: 0 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('common.company_wide_prompts')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>{renderPromptList(companyPrompts)}</AccordionDetails>
          </Accordion>

          {/* Domain-specific prompts */}
          <Accordion defaultExpanded={false} sx={{ mb: 0 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('common.domain_specific_prompts')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>{renderPromptList(domainPrompts)}</AccordionDetails>
          </Accordion>

          {/* My Prompts */}
          <Accordion defaultExpanded={true} sx={{ mb: 0 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('common.my_prompts')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>{renderPromptList(personalPrompts)}</AccordionDetails>
          </Accordion>
        </Box>
      </Drawer>

      {/* Confirmation for delete */}
      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('common.confirm_delete')}
        message={t('common.confirm_delete_prompt')}
        isLoading={isDeleteLoading}
      />

      {/* Edit modal */}
      {editPrompt && (
        <SavePromptDialog
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditPrompt(null);
          }}
          promptContent={editPrompt.content}
          companyId={companyId || ''}
          domainId={domainId}
          feature={feature}
          existingPrompt={editPrompt}
          onSaved={handleEditSave}
        />
      )}
    </>
  );
};

export default PromptsDrawer;
